import { useRef, useState } from 'react';
import Modal from './Modal';
import { ScanPane } from './DuplicateCompareDialog';
import { documentsApi, formatCurrency } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';

// One החברה המרכזית scan, two documents (#367, server/lib/combinedReturnNote.js):
// creates the return as a second draft on the same scan, with no rows.
//
// The number comes from the AI's read and the admin confirms it, so the scan is
// inside the dialog: covering the page with a scrim would hide the one thing it
// has to be compared with (owner, 02/11/2026).
//
// The server refuses a duplicate on submit; the answer stays on the field, and
// the button waits for a different number. `onCreated({ id, doc_num })`.
const ERROR_OF_CODE = {
  same_number: 'זה המספר של תעודת המשלוח · יש להקליד את מספר ההחזרה',
  number_required: 'יש להקליד את מספר ההחזרה',
};

export default function ReturnNoteDialog({ doc, files, onCreated, onOpenExisting, onClose }) {
  const readNumber = doc.return_note?.number || '';
  const [num, setNum] = useState(readNumber);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(null); // { num, message, existing? }
  // A number from the scan needs checking, not typing: focus goes to the button,
  // so a phone keeps its keyboard down and the scan in view. An empty field needs typing.
  const createRef = useRef(null);
  const inputRef = useRef(null);

  const trimmed = num.trim();
  const isOwnNumber = trimmed !== '' && trimmed === (doc.doc_num || '').trim();
  const refusal = refused?.num === trimmed ? refused : null;
  const message = isOwnNumber ? ERROR_OF_CODE.same_number : refusal?.message;
  // A refusal stands until the number changes; a failure (network, 500) can be retried.
  const canCreate = !!trimmed && !isOwnNumber && !(refusal && !refusal.canRetry) && !busy;

  const create = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setBusy(true);
    try {
      const r = await documentsApi.createReturnNote(doc.id, trimmed);
      onCreated(r.data);
    } catch (err) {
      const data = err?.response?.data || {};
      setRefused({
        num: trimmed,
        message: ERROR_OF_CODE[data.code] || data.error || 'יצירת תעודת ההחזרה נכשלה',
        existing: data.existing || null,
        canRetry: !data.code,
      });
      setBusy(false);
    }
  };

  let hint = 'יש לוודא שזה המספר שליד "ת.החזרה"';
  if (readNumber && trimmed === readNumber) hint = 'נקרא מהמסמך · יש לוודא שזה המספר שליד "ת.החזרה"';
  else if (!trimmed) hint = 'יש להקליד את המספר שליד "ת.החזרה" במסמך';

  return (
    <Modal title="צור תעודת החזרה בנוסף" size={1040} className="return-note-modal" onClose={onClose}
      initialFocus={readNumber ? createRef : inputRef} initialFocusOptions={{ preventScroll: true }} closeOnBackdrop={false}>
      <div className="return-note-grid">
        <form className="return-note-form" onSubmit={create}>
          <p className="return-note-msg">
            תיווצר טיוטה נפרדת על אותו מסמך מקורי, ללא שורות. תעודת המשלוח נשארת כפי שהיא.
          </p>
          <div className="form-group">
            <label htmlFor="return-note-num">מספר תעודת החזרה</label>
            <input
              ref={inputRef} id="return-note-num" className="form-control" dir="ltr" inputMode="numeric" autoComplete="off"
              value={num} onChange={e => setNum(e.target.value)}
              aria-invalid={!!message} aria-describedby="return-note-hint"
            />
            <div id="return-note-hint" className={`return-note-hint${message ? ' is-error' : ''}`} role={message ? 'alert' : undefined}>
              {message || hint}
              {refusal?.existing && (
                <>
                  {' · '}
                  <button type="button" className="return-note-open" onClick={() => onOpenExisting(refusal.existing.id)}>
                    פתח את המסמך
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="return-note-facts">
            <div className="return-note-fact"><span>ספק</span><span>{doc.supplier_name}</span></div>
            <div className="return-note-fact"><span>תאריך</span><span className="return-note-num">{formatDateShort(doc.date)}</span></div>
            <div className="return-note-fact"><span>מסמך מקורי</span><span>אותו מסמך{files.length > 1 ? ` · ${files.length} עמודים` : ''}</span></div>
            <div className="return-note-fact"><span>סכום</span><span>{formatCurrency(0)}</span></div>
          </div>
          <div className="modal-actions">
            <button ref={createRef} type="submit" className="btn btn-primary" disabled={!canCreate}>
              {busy ? 'יוצר...' : 'צור טיוטה'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>ביטול</button>
          </div>
        </form>
        <div className="return-note-scan">
          <ScanPane files={files} label="מסמך מקורי" head={<strong>מסמך מקורי</strong>} openable />
        </div>
      </div>
    </Modal>
  );
}
